import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  FileText, Globe, Receipt, Users, Wallet, CreditCard, 
  TrendingDown, PiggyBank, Package, Bell, Table as TableIcon,
  Smartphone, RefreshCw, HelpCircle, Eye, Clock, Languages,
  User, DollarSign, Calendar, History, MessageSquare, Bike,
  Monitor, Columns, UtensilsCrossed, Printer, Percent, 
  Settings, ToggleLeft, RotateCw, ListOrdered, X, ArrowLeft,
  Plus, Minus, Trash2, Gift, Search, Sun, Download, GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';
import OffersManager from '@/components/POS/OffersManager';
import CustomerHistoryLookup from '@/components/POS/CustomerHistoryLookup';


const POSDashboard = () => {
  const [user, setUser] = useState(null);
  const [showModule, setShowModule] = useState(null);
  const [showOffersManager, setShowOffersManager] = useState(false);
  const [showCustomerLookup, setShowCustomerLookup] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // For button reordering
  const [draggedModule, setDraggedModule] = useState(null); // For drag & drop
  const navigate = useNavigate();
  
  // Data states
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [riders, setRiders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [cashFlow, setCashFlow] = useState({ inflow: 0, outflow: 0, balance: 0 });
  
  // Day settings state
  const [daySettings, setDaySettings] = useState({
    day_start_time: '09:00',
    day_end_time: '23:00',
    is_day_started: false
  });
  const [showDayStartPopup, setShowDayStartPopup] = useState(false);
  
  // Tax configuration
  const [taxConfig, setTaxConfig] = useState({
    enabled: true,
    taxes: [
      { id: 1, name: 'CGST', percent: 2.5, enabled: true },
      { id: 2, name: 'SGST', percent: 2.5, enabled: true }
    ],
    serviceCharge: {
      enabled: true,
      percent: 5
    }
  });
  
  // NEW COMPREHENSIVE DISCOUNT SYSTEM
  // Each discount can have: mode restrictions, category discounts, item discounts, and fallback bill discount
  const [discountConfig, setDiscountConfig] = useState(() => {
    const saved = localStorage.getItem('pos_discounts_v2');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      discounts: [
        { 
          id: 1, 
          name: 'Dine-In Special', 
          type: 'percent',
          enabled: true,
          // Order mode restrictions
          allowedModes: ['DINE_IN'],
          // Category-wise discounts (multiple categories, custom %)
          categoryDiscounts: { 'Pizza': 15, 'Burger': 10 },
          // Item-level discounts (optional, overrides category)
          itemDiscounts: {},
          // Fallback bill-level discount (only if no item/category discount)
          billDiscount: 0
        },
        { 
          id: 2, 
          name: 'Takeaway Offer', 
          type: 'percent',
          enabled: true,
          allowedModes: ['TAKEAWAY'],
          categoryDiscounts: { 'Pizza': 20 },
          itemDiscounts: {},
          billDiscount: 5
        },
        { 
          id: 3, 
          name: 'Staff Discount', 
          type: 'percent',
          enabled: true,
          allowedModes: ['DINE_IN', 'TAKEAWAY', 'DELIVERY'],
          categoryDiscounts: {},
          itemDiscounts: {},
          billDiscount: 20
        }
      ]
    };
  });
  
  // Save discount config to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('pos_discounts_v2', JSON.stringify(discountConfig));
  }, [discountConfig]);
  
  // New discount form state for comprehensive discount creation
  const [discountForm, setDiscountForm] = useState({
    name: '',
    type: 'percent',
    allowedModes: ['DINE_IN'], // Default to Dine-In only
    categoryDiscounts: {}, // { 'Pizza': 30, 'Burger': 10 }
    itemDiscounts: {}, // { 'item-id-1': { name: 'Veg Pizza', percent: 35 } }
    billDiscount: 0
  });
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  
  // Available order modes for discount configuration
  const ORDER_MODES = [
    { id: 'DINE_IN', label: 'Dine-In', icon: '🍽️' },
    { id: 'TAKEAWAY', label: 'Takeaway', icon: '🥡' },
    { id: 'DELIVERY', label: 'Delivery', icon: '🛵' },
    { id: 'SWIGGY', label: 'Swiggy', icon: '🟠' },
    { id: 'ZOMATO', label: 'Zomato', icon: '🔴' }
  ];
  
  // Expense form
  const [expenseForm, setExpenseForm] = useState({ category: '', amount: '', description: '' });
  
  // Cash drawer
  const [cashDrawer, setCashDrawer] = useState({ opening: 0, current: 0, withdrawals: 0, topups: 0 });
  const [cashAction, setCashAction] = useState({ type: '', amount: '', reason: '' });
  
  // Customer edit/add
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState({ name: '', phone: '', address: '' });
  const [showCustomerForm, setShowCustomerForm] = useState(false);

  // Menu item form for creating/editing
  const [menuItemForm, setMenuItemForm] = useState({ 
    name: '', 
    category: '', 
    price: '', 
    description: '',
    short_code: '',
    active: true,
    is_combo: false,
    combo_items: []
  });
  const [editingMenuItem, setEditingMenuItem] = useState(null);
  const [showMenuItemForm, setShowMenuItemForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const loadDashboardData = async (storeId) => {
    try {
      // Load orders
      const ordersRes = await apiClient.get(`/orders?store_id=${storeId}&limit=100`);
      setOrders(ordersRes.data || []);
      
      // Calculate cash flow from orders
      const todayOrders = (ordersRes.data || []).filter(o => {
        const orderDate = new Date(o.created_at);
        const today = new Date();
        return orderDate.toDateString() === today.toDateString();
      });
      
      const inflow = todayOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      setCashFlow({ inflow, outflow: 0, balance: inflow });
      
      // Load riders
      try {
        const ridersRes = await apiClient.get(`/auth/riders?store_id=${storeId}`);
        setRiders(ridersRes.data || []);
      } catch (e) {
        setRiders([]);
      }
      
      // Load menu items
      try {
        const menuRes = await apiClient.get(`/menu/${storeId}`);
        setMenuItems(menuRes.data || []);
      } catch (e) {
        setMenuItems([]);
      }
      
      // Load categories from API
      try {
        const catRes = await apiClient.get(`/categories?store_id=${storeId}`);
        setCategories(catRes.data?.categories || []);
      } catch (e) {
        setCategories([]);
      }
      
      // Load customers from orders
      const customerMap = new Map();
      (ordersRes.data || []).forEach(order => {
        if (order.customer?.phone) {
          customerMap.set(order.customer.phone, {
            ...order.customer,
            lastOrder: order.created_at,
            totalOrders: (customerMap.get(order.customer.phone)?.totalOrders || 0) + 1
          });
        }
      });
      setCustomers(Array.from(customerMap.values()));
      
      // Load day settings
      try {
        const daySettingsRes = await apiClient.get(`/day-operations/settings/${storeId}`);
        setDaySettings(daySettingsRes.data || {
          day_start_time: '09:00',
          day_end_time: '23:00',
          is_day_started: false
        });
        
        // Show popup if day not started and first login
        if (!daySettingsRes.data?.is_day_started && !localStorage.getItem('day_popup_shown_today')) {
          setShowDayStartPopup(true);
          localStorage.setItem('day_popup_shown_today', new Date().toDateString());
        }
      } catch (e) {
        console.error('Error loading day settings:', e);
      }
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.id || !userData.store_id) {
      navigate('/login');
      return;
    }
    // Use functional update pattern
    const initializeData = async () => {
      setUser(userData);
      await loadDashboardData(userData.store_id);
    };
    initializeData();
  }, [navigate]);

  const handleAddExpense = async () => {
    if (!expenseForm.category || !expenseForm.amount) {
      toast.error('Please fill all required fields');
      return;
    }
    
    const newExpense = {
      id: Date.now(),
      category: expenseForm.category,
      amount: parseFloat(expenseForm.amount),
      description: expenseForm.description,
      date: new Date().toISOString(),
      addedBy: user?.name
    };
    
    setExpenses([...expenses, newExpense]);
    setCashFlow(prev => ({ 
      ...prev, 
      outflow: prev.outflow + newExpense.amount,
      balance: prev.balance - newExpense.amount
    }));
    setExpenseForm({ category: '', amount: '', description: '' });
    toast.success('Expense added');
  };

  const handleCashAction = () => {
    if (!cashAction.amount) {
      toast.error('Please enter amount');
      return;
    }
    
    const amount = parseFloat(cashAction.amount);
    
    if (cashAction.type === 'withdrawal') {
      setCashDrawer(prev => ({ ...prev, withdrawals: prev.withdrawals + amount, current: prev.current - amount }));
      setCashFlow(prev => ({ ...prev, outflow: prev.outflow + amount, balance: prev.balance - amount }));
      toast.success(`Withdrawn ₹${amount}`);
    } else if (cashAction.type === 'topup') {
      setCashDrawer(prev => ({ ...prev, topups: prev.topups + amount, current: prev.current + amount }));
      setCashFlow(prev => ({ ...prev, inflow: prev.inflow + amount, balance: prev.balance + amount }));
      toast.success(`Added ₹${amount} to drawer`);
    }
    
    setCashAction({ type: '', amount: '', reason: '' });
  };

  const handleSaveTaxConfig = async () => {
    try {
      // Calculate total tax percent from enabled taxes
      const totalPercent = taxConfig.taxes
        .filter(t => t.enabled)
        .reduce((sum, t) => sum + t.percent, 0);
      
      // Use the correct endpoint and format
      // When main tax toggle is OFF, both taxes AND service charge are disabled
      await apiClient.put(`/stores/${user?.store_id}/config`, {
        tax_enabled: taxConfig.enabled,
        tax_percent: totalPercent,
        tax_breakdown: taxConfig.taxes,
        // Service charge follows the main tax toggle
        service_charge_enabled: taxConfig.enabled && taxConfig.serviceCharge?.enabled,
        service_charge_percent: taxConfig.serviceCharge?.percent || 0
      });
      toast.success('Tax & Service Charge configuration saved!');
      setShowModule(null);
    } catch (error) {
      console.error('Tax config error:', error);
      toast.error('Failed to save tax config');
    }
  };

  const handleToggleMenuItem = async (itemId, currentActive) => {
    try {
      await apiClient.put(`/menu/${user?.store_id}/${itemId}`, {
        active: !currentActive
      });
      setMenuItems(menuItems.map(item => 
        item.id === itemId ? { ...item, active: !currentActive } : item
      ));
      toast.success(`Item ${!currentActive ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  // Customer handlers
  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      name: customer.name || '',
      phone: customer.phone || '',
      address: customer.address || ''
    });
    setShowCustomerForm(true);
  };

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setCustomerForm({ name: '', phone: '', address: '' });
    setShowCustomerForm(true);
  };

  const handleSaveCustomer = async () => {
    try {
      if (!customerForm.phone) {
        toast.error('Phone number is required');
        return;
      }

      if (editingCustomer) {
        // Update existing customer
        await apiClient.put(`/customers/${editingCustomer.id || editingCustomer.customer_id}`, {
          name: customerForm.name,
          phone: customerForm.phone,
          address: customerForm.address
        });
        // Update local state
        setCustomers(customers.map(c => 
          (c.phone === editingCustomer.phone) 
            ? { ...c, name: customerForm.name, phone: customerForm.phone, address: customerForm.address }
            : c
        ));
        toast.success('Customer updated!');
      } else {
        // Create new customer
        await apiClient.post('/customers', {
          store_id: user?.store_id,
          name: customerForm.name,
          phone: customerForm.phone,
          address: customerForm.address
        });
        // Reload customers
        loadDashboardData(user?.store_id);
        toast.success('Customer added!');
      }
      
      setShowCustomerForm(false);
      setEditingCustomer(null);
      setCustomerForm({ name: '', phone: '', address: '' });
    } catch (error) {
      console.error('Save customer error:', error);
      toast.error('Failed to save customer');
    }
  };

  // Category handlers
  const handleAddCategory = async (categoryName) => {
    try {
      await apiClient.post('/categories', {
        store_id: user?.store_id,
        name: categoryName
      });
      toast.success(`Category '${categoryName}' created!`);
      loadDashboardData(user?.store_id);
    } catch (error) {
      console.error('Add category error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    const itemCount = menuItems.filter(item => item.category === categoryName).length;
    const confirmed = window.confirm(
      `Are you sure you want to DELETE "${categoryName}" category?\n\nThis will PERMANENTLY DELETE ${itemCount} item(s) in this category.\n\nThis action cannot be undone!`
    );
    
    if (!confirmed) return;
    
    try {
      await apiClient.delete(`/categories/${encodeURIComponent(categoryName)}?store_id=${user?.store_id}`);
      toast.success(`Category '${categoryName}' deleted with ${itemCount} items`);
      loadDashboardData(user?.store_id);
    } catch (error) {
      console.error('Delete category error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const handleDeleteMenuItem = async (itemId, itemName) => {
    const confirmed = window.confirm(
      `Are you sure you want to PERMANENTLY DELETE "${itemName}"?\n\nThis action cannot be undone!`
    );
    
    if (!confirmed) return;
    
    try {
      await apiClient.delete(`/menu/${itemId}`);
      setMenuItems(menuItems.filter(item => item.id !== itemId));
      toast.success(`Item '${itemName}' permanently deleted`);
    } catch (error) {
      console.error('Delete item error:', error);
      toast.error('Failed to delete item');
    }
  };

  // Menu item handlers
  const handleAddMenuItem = () => {
    setEditingMenuItem(null);
    setMenuItemForm({ 
      name: '', 
      category: '', 
      price: '', 
      description: '',
      short_code: '',
      active: true,
      is_combo: false,
      combo_items: []
    });
    setShowMenuItemForm(true);
  };

  const handleEditMenuItem = (item) => {
    setEditingMenuItem(item);
    setMenuItemForm({
      name: item.name || '',
      category: item.category || '',
      price: item.price?.toString() || '',
      description: item.description || '',
      short_code: item.short_code || '',
      active: item.active !== false,
      is_combo: item.is_combo || false,
      combo_items: item.combo_items || []
    });
    setShowMenuItemForm(true);
  };

  const handleSaveMenuItem = async () => {
    if (!menuItemForm.name || !menuItemForm.category || !menuItemForm.price) {
      toast.error('Please fill Name, Category, and Price');
      return;
    }

    const price = parseFloat(menuItemForm.price);
    if (isNaN(price) || price < 0) {
      toast.error('Please enter a valid price');
      return;
    }

    try {
      if (editingMenuItem) {
        // Update existing item
        await apiClient.put(`/menu/${user?.store_id}/${editingMenuItem.id}`, {
          name: menuItemForm.name,
          category: menuItemForm.category,
          price: price,
          description: menuItemForm.description,
          short_code: menuItemForm.short_code,
          active: menuItemForm.active,
          is_combo: menuItemForm.is_combo,
          combo_items: menuItemForm.combo_items
        });
        setMenuItems(menuItems.map(item => 
          item.id === editingMenuItem.id 
            ? { ...item, ...menuItemForm, price, is_combo: menuItemForm.is_combo, combo_items: menuItemForm.combo_items }
            : item
        ));
        toast.success('Menu item updated!');
      } else {
        // Create new item - generate SKU from name
        const sku = menuItemForm.name.toUpperCase().replace(/[^A-Z0-9]/g, '_').substring(0, 15) + '_' + Date.now().toString(36).toUpperCase();
        const response = await apiClient.post(`/menu?store_id=${user?.store_id}`, {
          sku: sku,
          name: menuItemForm.name,
          category: menuItemForm.category,
          price: price,
          description: menuItemForm.description,
          short_code: menuItemForm.short_code,
          active: menuItemForm.active,
          is_combo: menuItemForm.is_combo,
          combo_items: menuItemForm.combo_items
        });
        if (response.data) {
          setMenuItems([...menuItems, response.data]);
        }
        toast.success('Menu item created!');
      }
      
      setShowMenuItemForm(false);
      setEditingMenuItem(null);
      setMenuItemForm({ name: '', category: '', price: '', description: '', short_code: '', active: true, is_combo: false, combo_items: [] });
    } catch (error) {
      console.error('Save menu item error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save menu item');
    }
  };

  // Discount handlers - COMPREHENSIVE DISCOUNT SYSTEM
  const handleAddDiscount = () => {
    setEditingDiscount(null);
    setDiscountForm({
      name: '',
      type: 'percent',
      allowedModes: ['DINE_IN'],
      categoryDiscounts: {},
      itemDiscounts: {},
      billDiscount: 0
    });
    setShowDiscountForm(true);
  };

  const handleEditDiscount = (discount) => {
    setEditingDiscount(discount);
    setDiscountForm({
      name: discount.name,
      type: discount.type || 'percent',
      allowedModes: discount.allowedModes || ['DINE_IN'],
      categoryDiscounts: discount.categoryDiscounts || {},
      itemDiscounts: discount.itemDiscounts || {},
      billDiscount: discount.billDiscount || 0
    });
    setShowDiscountForm(true);
  };

  const handleSaveDiscount = () => {
    if (!discountForm.name) {
      toast.error('Please enter a discount name');
      return;
    }

    if (discountForm.allowedModes.length === 0) {
      toast.error('Please select at least one order mode');
      return;
    }

    // Validate at least one discount type is set
    const hasCategoryDiscount = Object.values(discountForm.categoryDiscounts).some(v => v > 0);
    const hasItemDiscount = Object.values(discountForm.itemDiscounts).some(v => v.percent > 0);
    const hasBillDiscount = discountForm.billDiscount > 0;

    if (!hasCategoryDiscount && !hasItemDiscount && !hasBillDiscount) {
      toast.error('Please set at least one discount (category, item, or bill-level)');
      return;
    }

    if (editingDiscount) {
      // Update existing discount
      setDiscountConfig(prev => ({
        ...prev,
        discounts: prev.discounts.map(d => 
          d.id === editingDiscount.id 
            ? { 
                ...d, 
                name: discountForm.name, 
                type: discountForm.type,
                allowedModes: discountForm.allowedModes,
                categoryDiscounts: discountForm.categoryDiscounts,
                itemDiscounts: discountForm.itemDiscounts,
                billDiscount: discountForm.billDiscount
              }
            : d
        )
      }));
      toast.success('Discount updated!');
    } else {
      // Create new comprehensive discount
      const newDiscount = {
        id: Date.now(),
        name: discountForm.name,
        type: discountForm.type,
        enabled: true,
        allowedModes: discountForm.allowedModes,
        categoryDiscounts: discountForm.categoryDiscounts,
        itemDiscounts: discountForm.itemDiscounts,
        billDiscount: discountForm.billDiscount
      };
      setDiscountConfig(prev => ({
        ...prev,
        discounts: [...prev.discounts, newDiscount]
      }));
      toast.success('Discount created!');
    }

    setShowDiscountForm(false);
    setEditingDiscount(null);
    setDiscountForm({
      name: '',
      type: 'percent',
      allowedModes: ['DINE_IN'],
      categoryDiscounts: {},
      itemDiscounts: {},
      billDiscount: 0
    });
  };

  const handleDeleteDiscount = (discountId) => {
    if (!window.confirm('Are you sure you want to delete this discount?')) return;
    
    setDiscountConfig(prev => ({
      ...prev,
      discounts: prev.discounts.filter(d => d.id !== discountId)
    }));
    toast.success('Discount deleted');
  };

  const handleToggleDiscount = (discountId) => {
    setDiscountConfig(prev => ({
      ...prev,
      discounts: prev.discounts.map(d => 
        d.id === discountId ? { ...d, enabled: !d.enabled } : d
      )
    }));
  };

  const handleAdjustDiscountValue = (discountId, adjustment) => {
    setDiscountConfig(prev => ({
      ...prev,
      discounts: prev.discounts.map(d => {
        if (d.id !== discountId) return d;
        const newValue = Math.max(0, d.value + adjustment);
        // Cap percentage at 100
        if (d.type === 'percent' && newValue > 100) return d;
        return { ...d, value: newValue };
      })
    }));
  };

  // ALL Dashboard Modules - Unified for full customization
  const allDefaultModules = [
    // Main modules (Row 1)
    { id: 'orders', icon: FileText, label: 'Orders', color: 'bg-blue-500' },
    { id: 'online-orders', icon: Globe, label: 'Online Orders', color: 'bg-green-500' },
    { id: 'kots', icon: Receipt, label: 'KOTs', color: 'bg-orange-500' },
    { id: 'customers', icon: Users, label: 'Customers', color: 'bg-purple-500' },
    { id: 'customer-lookup', icon: Search, label: 'Customer Lookup', color: 'bg-pink-500' },
    { id: 'offers', icon: Gift, label: 'Offers & Discounts', color: 'bg-rose-500' },
    { id: 'cash-flow', icon: Wallet, label: 'Cash Flow', color: 'bg-emerald-500' },
    { id: 'expense', icon: TrendingDown, label: 'Expense', color: 'bg-red-500' },
    { id: 'withdrawal', icon: CreditCard, label: 'Withdrawal', color: 'bg-amber-500' },
    { id: 'cash-topup', icon: PiggyBank, label: 'Cash Top-Up', color: 'bg-teal-500' },
    { id: 'inventory', icon: Package, label: 'Inventory', color: 'bg-indigo-500' },
    // Second row
    { id: 'alerts', icon: Bell, label: 'Alerts', color: 'bg-red-400' },
    { id: 'table', icon: TableIcon, label: 'Table', color: 'bg-cyan-500' },
    { id: 'virtual-wallet', icon: Smartphone, label: 'Virtual Wallet', color: 'bg-violet-500' },
    { id: 'manual-sync', icon: RefreshCw, label: 'Manual Sync', color: 'bg-gray-500' },
    { id: 'help', icon: HelpCircle, label: 'Help', color: 'bg-blue-400' },
    { id: 'live-view', icon: Eye, label: 'Live View', color: 'bg-green-400' },
    { id: 'due-payment', icon: Clock, label: 'Due Payment', color: 'bg-orange-400' },
    { id: 'language', icon: Languages, label: 'Language', color: 'bg-pink-500' },
    { id: 'billing-user', icon: User, label: 'Billing User', color: 'bg-slate-500' },
    // Third row
    { id: 'currency', icon: DollarSign, label: 'Currency', color: 'bg-yellow-500' },
    { id: 'table-reservation', icon: Calendar, label: 'Reservation', color: 'bg-rose-500' },
    { id: 'day-end', icon: History, label: 'Day End', color: 'bg-fuchsia-500' },
    { id: 'day-end-history', icon: History, label: 'Day History', color: 'bg-lime-500' },
    { id: 'feedback', icon: MessageSquare, label: 'Feedback', color: 'bg-sky-500' },
    { id: 'delivery-boys', icon: Bike, label: 'Delivery Boys', color: 'bg-amber-600' },
    { id: 'led-display', icon: Monitor, label: 'LED Display', color: 'bg-zinc-500' },
    { id: 'dual-screen', icon: Columns, label: 'Dual Screen', color: 'bg-stone-500' },
    // Config modules
    { id: 'menu', icon: UtensilsCrossed, label: 'Menu', color: 'bg-orange-600' },
    { id: 'categories-config', icon: ListOrdered, label: 'Categories', color: 'bg-violet-600' },
    { id: 'bill-print', icon: Printer, label: 'Bill/KOT Print', color: 'bg-blue-600' },
    { id: 'tax', icon: Percent, label: 'Tax', color: 'bg-green-600' },
    { id: 'discount', icon: Percent, label: 'Discount', color: 'bg-purple-600' },
    { id: 'billing-screen', icon: FileText, label: 'Billing Screen', color: 'bg-cyan-600' },
    { id: 'settings', icon: Settings, label: 'Settings', color: 'bg-gray-600' },
    { id: 'menu-on-off', icon: ToggleLeft, label: 'Menu On/Off', color: 'bg-red-600' },
    { id: 'service-renewal', icon: RotateCw, label: 'Service Renewal', color: 'bg-indigo-600' },
    { id: 'custom-status', icon: ListOrdered, label: 'Order Status', color: 'bg-teal-600' },
  ];

  // Load custom order from localStorage - for ALL modules
  const [allModuleOrder, setAllModuleOrder] = useState(() => {
    const saved = localStorage.getItem('pos_all_module_order');
    return saved ? JSON.parse(saved) : allDefaultModules.map(m => m.id);
  });

  // Sort ALL modules by saved order
  const allModules = [...allDefaultModules].sort((a, b) => {
    const indexA = allModuleOrder.indexOf(a.id);
    const indexB = allModuleOrder.indexOf(b.id);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  const resetModuleOrder = () => {
    const defaultOrder = allDefaultModules.map(m => m.id);
    setAllModuleOrder(defaultOrder);
    localStorage.setItem('pos_all_module_order', JSON.stringify(defaultOrder));
    toast.success('Dashboard layout reset to default');
  };

  const handleModuleClick = (moduleId) => {
    switch(moduleId) {
      case 'orders':
      case 'online-orders':
      case 'kots':
      case 'live-view':
        navigate('/pos');
        break;
      case 'offers':
        setShowOffersManager(true);
        break;
      case 'customer-lookup':
        setShowCustomerLookup(true);
        break;
      case 'menu':
      case 'delivery-boys':
      case 'customers':
      case 'cash-flow':
      case 'expense':
      case 'withdrawal':
      case 'cash-topup':
      case 'tax':
      case 'discount':
      case 'menu-on-off':
      case 'day-end':
      case 'categories-config':
      case 'categories':
        setShowModule(moduleId);
        break;
      default:
        toast.info(`${moduleId} module coming soon`);
    }
  };

  // Render unified module grid with DRAG & DROP capability
  const handleDragStart = (e, moduleId) => {
    setDraggedModule(moduleId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', moduleId);
  };

  const handleDragOver = (e, targetModuleId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetModuleId) => {
    e.preventDefault();
    if (!draggedModule || draggedModule === targetModuleId) return;
    
    const currentOrder = [...allModuleOrder];
    const draggedIndex = currentOrder.indexOf(draggedModule);
    const targetIndex = currentOrder.indexOf(targetModuleId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Remove dragged item and insert at target position
    currentOrder.splice(draggedIndex, 1);
    currentOrder.splice(targetIndex, 0, draggedModule);
    
    setAllModuleOrder(currentOrder);
    localStorage.setItem('pos_all_module_order', JSON.stringify(currentOrder));
    setDraggedModule(null);
    toast.success('Layout updated');
  };

  const handleDragEnd = () => {
    setDraggedModule(null);
  };

  const renderUnifiedDashboard = () => (
    <div className="mb-6">
      {/* Header with Reorder Controls */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-600">Dashboard Modules ({allModules.length} items)</h2>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant={isEditMode ? "default" : "outline"}
            onClick={() => setIsEditMode(!isEditMode)}
            className="h-7"
          >
            <GripVertical className="h-3 w-3 mr-1" />
            {isEditMode ? 'Done Editing' : 'Customize Layout'}
          </Button>
          {isEditMode && (
            <Button 
              size="sm" 
              variant="outline" 
              onClick={resetModuleOrder}
              className="h-7 text-red-600 border-red-300 hover:bg-red-50"
            >
              Reset to Default
            </Button>
          )}
        </div>
      </div>
      
      {isEditMode && (
        <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded mb-3">
          🖱️ <strong>Drag & Drop</strong> any icon to reorder. Your layout will be saved automatically.
        </p>
      )}
      
      {/* Unified Grid - All modules with drag & drop */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-3">
        {allModules.map((module, index) => (
          <Card 
            key={module.id}
            draggable={isEditMode}
            onDragStart={(e) => isEditMode && handleDragStart(e, module.id)}
            onDragOver={(e) => isEditMode && handleDragOver(e, module.id)}
            onDrop={(e) => isEditMode && handleDrop(e, module.id)}
            onDragEnd={handleDragEnd}
            className={`cursor-pointer hover:shadow-lg transition-all relative
              ${!isEditMode ? 'hover:scale-105' : 'ring-2 ring-blue-200 cursor-grab active:cursor-grabbing'}
              ${draggedModule === module.id ? 'opacity-50 scale-95' : ''}
              ${isEditMode && draggedModule && draggedModule !== module.id ? 'hover:ring-green-400 hover:bg-green-50' : ''}
            `}
            onClick={() => !isEditMode && handleModuleClick(module.id)}
          >
            <CardContent className="p-3 flex flex-col items-center justify-center text-center">
              <div className={`${module.color} p-2 rounded-lg mb-2`}>
                <module.icon className="h-5 w-5 text-white" />
              </div>
              <span className="text-[10px] font-medium text-gray-700 leading-tight">{module.label}</span>
              
              {/* Drag handle indicator in edit mode */}
              {isEditMode && (
                <GripVertical className="absolute top-1 right-1 h-3 w-3 text-gray-400" />
              )}
              
              {/* Position indicator in edit mode */}
              {isEditMode && (
                <span className="absolute -top-1 -left-1 bg-blue-500 text-white text-[8px] rounded-full w-4 h-4 flex items-center justify-center">
                  {index + 1}
                </span>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#C41E3A] text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UtensilsCrossed className="h-6 w-6" />
            <div>
              <h1 className="font-bold text-lg">SSPICE POS Dashboard</h1>
              <span className="text-xs opacity-80">(Designed by Prashant B)</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={() => navigate('/pos')}>
              New Order
            </Button>
            <Button size="sm" variant="outline" className="text-white border-white hover:bg-red-700" onClick={() => navigate('/login')}>
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4">
        {renderUnifiedDashboard()}
      </div>

      {/* Delivery Boys Modal */}
      <Dialog open={showModule === 'delivery-boys'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bike className="h-5 w-5" /> Delivery Boys Management
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {riders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No riders found</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {riders.map((rider) => (
                    <TableRow key={rider.id}>
                      <TableCell className="font-medium">{rider.name}</TableCell>
                      <TableCell>{rider.phone}</TableCell>
                      <TableCell>
                        <Badge className={rider.active ? 'bg-green-500' : 'bg-gray-500'}>
                          {rider.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" onClick={() => toast.info('Track rider coming soon')}>
                          Track
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Customers Modal */}
      <Dialog open={showModule === 'customers'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="h-5 w-5" /> Customer Management
              </span>
              <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={handleAddCustomer}>
                <Plus className="h-4 w-4 mr-1" /> Add Customer
              </Button>
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {customers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No customers found
                <Button variant="outline" className="mt-4 block mx-auto" onClick={handleAddCustomer}>
                  <Plus className="h-4 w-4 mr-1" /> Add First Customer
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Orders</TableHead>
                    <TableHead>Last Order</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((customer, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{customer.name || 'N/A'}</TableCell>
                      <TableCell>{customer.phone}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{customer.address || 'N/A'}</TableCell>
                      <TableCell>{customer.totalOrders}</TableCell>
                      <TableCell className="text-xs">{new Date(customer.lastOrder).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7"
                          onClick={() => handleEditCustomer(customer)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Customer Edit/Add Form Modal */}
      <Dialog open={showCustomerForm} onOpenChange={() => setShowCustomerForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Customer Name</Label>
              <Input
                placeholder="Enter full name"
                value={customerForm.name}
                onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input
                type="tel"
                placeholder="10-digit mobile number"
                value={customerForm.phone}
                onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                maxLength={10}
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                placeholder="Full address"
                value={customerForm.address}
                onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerForm(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleSaveCustomer}>
              {editingCustomer ? 'Save Changes' : 'Add Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash Flow Modal */}
      <Dialog open={showModule === 'cash-flow'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" /> Today&apos;s Cash Flow
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <p className="text-sm text-green-600">Total Inflow</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(cashFlow.inflow)}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <p className="text-sm text-red-600">Total Outflow</p>
                <p className="text-2xl font-bold text-red-700">{formatCurrency(cashFlow.outflow)}</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <p className="text-sm text-blue-600">Net Balance</p>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(cashFlow.balance)}</p>
              </CardContent>
            </Card>
            <div className="text-xs text-gray-500 text-center">
              Based on {orders.filter(o => new Date(o.created_at).toDateString() === new Date().toDateString()).length} orders today
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expense Modal */}
      <Dialog open={showModule === 'expense'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5" /> Expense Management
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({...expenseForm, category: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ingredients">Ingredients</SelectItem>
                    <SelectItem value="utilities">Utilities</SelectItem>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="salary">Salary</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount (₹)</Label>
                <Input 
                  type="number" 
                  placeholder="0.00"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                placeholder="Enter description"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
              />
            </div>
            <Button className="w-full bg-red-500 hover:bg-red-600" onClick={handleAddExpense}>
              <Plus className="h-4 w-4 mr-2" /> Add Expense
            </Button>
            
            {expenses.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium mb-2">Today&apos;s Expenses</h4>
                <ScrollArea className="max-h-40">
                  {expenses.map((exp) => (
                    <div key={exp.id} className="flex justify-between items-center p-2 bg-gray-50 rounded mb-1">
                      <div>
                        <p className="font-medium text-sm">{exp.category}</p>
                        <p className="text-xs text-gray-500">{exp.description}</p>
                      </div>
                      <span className="font-bold text-red-600">{formatCurrency(exp.amount)}</span>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdrawal Modal */}
      <Dialog open={showModule === 'withdrawal'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" /> Cash Withdrawal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount (₹)</Label>
              <Input 
                type="number" 
                placeholder="Enter amount"
                value={cashAction.amount}
                onChange={(e) => setCashAction({...cashAction, type: 'withdrawal', amount: e.target.value})}
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Input 
                placeholder="Reason for withdrawal"
                value={cashAction.reason}
                onChange={(e) => setCashAction({...cashAction, reason: e.target.value})}
              />
            </div>
            <Button className="w-full bg-amber-500 hover:bg-amber-600" onClick={handleCashAction}>
              <Minus className="h-4 w-4 mr-2" /> Withdraw
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash Top-Up Modal */}
      <Dialog open={showModule === 'cash-topup'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PiggyBank className="h-5 w-5" /> Cash Top-Up
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount (₹)</Label>
              <Input 
                type="number" 
                placeholder="Enter amount"
                value={cashAction.amount}
                onChange={(e) => setCashAction({...cashAction, type: 'topup', amount: e.target.value})}
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Input 
                placeholder="Reason for top-up"
                value={cashAction.reason}
                onChange={(e) => setCashAction({...cashAction, reason: e.target.value})}
              />
            </div>
            <Button className="w-full bg-teal-500 hover:bg-teal-600" onClick={handleCashAction}>
              <Plus className="h-4 w-4 mr-2" /> Add Cash
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tax Configuration Modal */}
      <Dialog open={showModule === 'tax'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5" /> Tax & Service Charge Configuration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Main Tax Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg">
              <div>
                <Label className="font-semibold">Enable Taxes & Charges</Label>
                <p className="text-xs text-gray-500">When OFF, no taxes or service charge will apply</p>
              </div>
              <Switch 
                checked={taxConfig.enabled}
                onCheckedChange={(checked) => setTaxConfig({...taxConfig, enabled: checked})}
              />
            </div>
            
            {taxConfig.enabled && (
              <div className="space-y-3">
                {/* Tax Items */}
                <p className="text-sm font-medium text-gray-600">Tax Breakdown</p>
                {taxConfig.taxes.map((tax, idx) => (
                  <div key={tax.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                    <Switch 
                      checked={tax.enabled}
                      onCheckedChange={(checked) => {
                        const newTaxes = [...taxConfig.taxes];
                        newTaxes[idx].enabled = checked;
                        setTaxConfig({...taxConfig, taxes: newTaxes});
                      }}
                    />
                    <div className="flex-1">
                      <Input 
                        value={tax.name}
                        onChange={(e) => {
                          const newTaxes = [...taxConfig.taxes];
                          newTaxes[idx].name = e.target.value;
                          setTaxConfig({...taxConfig, taxes: newTaxes});
                        }}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="w-20">
                      <Input 
                        type="number"
                        value={tax.percent}
                        onChange={(e) => {
                          const newTaxes = [...taxConfig.taxes];
                          newTaxes[idx].percent = parseFloat(e.target.value) || 0;
                          setTaxConfig({...taxConfig, taxes: newTaxes});
                        }}
                        className="h-8 text-sm"
                      />
                    </div>
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                ))}
                
                <div className="flex justify-between p-3 bg-blue-50 rounded">
                  <span className="font-medium">Total Tax</span>
                  <span className="font-bold text-blue-600">
                    {taxConfig.taxes.filter(t => t.enabled).reduce((sum, t) => sum + t.percent, 0)}%
                  </span>
                </div>

                {/* Service Charge Section */}
                <div className="border-t pt-3 mt-3">
                  <p className="text-sm font-medium text-gray-600 mb-2">Service Charge</p>
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded">
                    <Switch 
                      checked={taxConfig.serviceCharge?.enabled || false}
                      onCheckedChange={(checked) => setTaxConfig({
                        ...taxConfig, 
                        serviceCharge: { ...taxConfig.serviceCharge, enabled: checked }
                      })}
                    />
                    <span className="flex-1 text-sm">Service Charge</span>
                    <div className="w-20">
                      <Input 
                        type="number"
                        value={taxConfig.serviceCharge?.percent || 0}
                        onChange={(e) => setTaxConfig({
                          ...taxConfig,
                          serviceCharge: { ...taxConfig.serviceCharge, percent: parseFloat(e.target.value) || 0 }
                        })}
                        className="h-8 text-sm"
                        disabled={!taxConfig.serviceCharge?.enabled}
                      />
                    </div>
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                </div>
              </div>
            )}
            
            {!taxConfig.enabled && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                <p className="text-sm text-yellow-800">⚠️ All taxes and service charges are currently DISABLED</p>
                <p className="text-xs text-yellow-600 mt-1">Bills will show base prices only</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModule(null)}>Cancel</Button>
            <Button className="bg-green-600" onClick={handleSaveTaxConfig}>Save Configuration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount Configuration Modal - COMPREHENSIVE SYSTEM */}
      <Dialog open={showModule === 'discount'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Percent className="h-5 w-5" /> Discount Management
              </span>
              <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={handleAddDiscount}>
                <Plus className="h-4 w-4 mr-1" /> Create Discount
              </Button>
            </DialogTitle>
            <p className="text-xs text-gray-500">Configure discounts with category, item, and order mode rules</p>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh]">
            <div className="space-y-3 py-4">
              {discountConfig.discounts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Percent className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No discounts configured</p>
                  <Button variant="outline" className="mt-3" onClick={handleAddDiscount}>
                    <Plus className="h-4 w-4 mr-1" /> Create First Discount
                  </Button>
                </div>
              ) : (
                discountConfig.discounts.map((discount) => (
                  <div key={discount.id} className={`p-4 rounded-lg border ${discount.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Switch 
                          checked={discount.enabled}
                          onCheckedChange={() => handleToggleDiscount(discount.id)}
                        />
                        <div>
                          <p className="font-bold text-sm">{discount.name}</p>
                          <Badge variant="outline" className="mt-1 text-[10px]">
                            {discount.type === 'percent' ? 'Percentage' : 'Fixed'}
                          </Badge>
                        </div>
                      </div>
                      <Badge variant={discount.enabled ? 'default' : 'outline'} className={discount.enabled ? 'bg-green-500' : ''}>
                        {discount.enabled ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    
                    {/* Order Modes */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {ORDER_MODES.map(mode => {
                        const isAllowed = discount.allowedModes?.includes(mode.id);
                        return (
                          <span key={mode.id} className={`text-[10px] px-2 py-0.5 rounded-full ${isAllowed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500 line-through'}`}>
                            {mode.icon} {mode.label}
                          </span>
                        );
                      })}
                    </div>
                    
                    {/* Discount Summary */}
                    <div className="bg-white rounded-lg p-2 border text-xs space-y-1">
                      {discount.categoryDiscounts && Object.keys(discount.categoryDiscounts).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          <span className="text-gray-500">Categories:</span>
                          {Object.entries(discount.categoryDiscounts).map(([cat, pct]) => (
                            <Badge key={cat} variant="outline" className="text-[10px]">{cat}: {pct}%</Badge>
                          ))}
                        </div>
                      )}
                      {discount.itemDiscounts && Object.keys(discount.itemDiscounts).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          <span className="text-gray-500">Items:</span>
                          {Object.entries(discount.itemDiscounts).map(([id, item]) => (
                            <Badge key={id} variant="secondary" className="text-[10px]">{item.name}: {item.percent}%</Badge>
                          ))}
                        </div>
                      )}
                      {discount.billDiscount > 0 && (
                        <div>
                          <span className="text-gray-500">Bill Fallback:</span>
                          <Badge variant="outline" className="ml-1 text-[10px]">{discount.billDiscount}%</Badge>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-3 pt-3 border-t">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => handleEditDiscount(discount)}>
                        <Settings className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteDiscount(discount.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button onClick={() => { setShowModule(null); toast.success('Discount configuration saved!'); }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* COMPREHENSIVE Add/Edit Discount Form Dialog */}
      <Dialog open={showDiscountForm} onOpenChange={() => setShowDiscountForm(false)}>
        <DialogContent className="max-w-xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editingDiscount ? 'Edit Discount' : 'Create New Discount'}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-5 py-4">
              {/* Discount Name */}
              <div>
                <Label className="text-sm font-medium">Discount Name *</Label>
                <Input 
                  placeholder="e.g., Weekend Special, Happy Hour"
                  value={discountForm.name}
                  onChange={(e) => setDiscountForm({ ...discountForm, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              {/* Discount Type */}
              <div>
                <Label className="text-sm font-medium">Discount Type *</Label>
                <Select value={discountForm.type} onValueChange={(v) => setDiscountForm({ ...discountForm, type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* ORDER MODE RESTRICTIONS */}
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <Label className="text-sm font-medium">Applies To (Order Modes) *</Label>
                <p className="text-xs text-gray-500 mb-2">Select which order types this discount applies to</p>
                <div className="flex flex-wrap gap-2">
                  {ORDER_MODES.map(mode => {
                    const isAllowed = discountForm.allowedModes?.includes(mode.id);
                    return (
                      <Button
                        key={mode.id}
                        type="button"
                        size="sm"
                        variant={isAllowed ? 'default' : 'outline'}
                        className={`h-8 ${isAllowed ? 'bg-green-500 hover:bg-green-600' : 'text-red-500 border-red-200'}`}
                        onClick={() => {
                          const newModes = isAllowed
                            ? discountForm.allowedModes.filter(m => m !== mode.id)
                            : [...(discountForm.allowedModes || []), mode.id];
                          setDiscountForm({ ...discountForm, allowedModes: newModes });
                        }}
                      >
                        {mode.icon} {mode.label}
                        {isAllowed ? ' ✓' : ' ✗'}
                      </Button>
                    );
                  })}
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  ⚠️ Swiggy/Zomato orders have higher menu prices. Disable discounts for aggregators to protect revenue.
                </p>
              </div>

              {/* CATEGORY-WISE DISCOUNTS */}
              <div className="border rounded-lg p-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  📁 Category Discounts
                  <span className="text-xs text-gray-400 font-normal">(Multiple categories allowed)</span>
                </Label>
                <p className="text-xs text-gray-500 mb-3">Set custom discount % for each category</p>
                <div className="space-y-2 max-h-[150px] overflow-y-auto">
                  {categories.map((cat, idx) => {
                    const catName = typeof cat === 'string' ? cat : cat.name;
                    const currentPct = discountForm.categoryDiscounts?.[catName] || 0;
                    return (
                      <div key={catName || idx} className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded">
                        <span className="text-sm font-medium">{catName}</span>
                        <div className="flex items-center gap-1">
                          <Input 
                            type="number"
                            min="0"
                            max="100"
                            value={currentPct || ''}
                            placeholder="0"
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setDiscountForm(prev => ({
                                ...prev,
                                categoryDiscounts: {
                                  ...prev.categoryDiscounts,
                                  [catName]: val
                                }
                              }));
                            }}
                            className="w-20 h-8 text-center"
                          />
                          <span className="text-sm text-gray-500">%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ITEM-LEVEL DISCOUNTS */}
              <div className="border rounded-lg p-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  🏷️ Item Discounts
                  <span className="text-xs text-gray-400 font-normal">(Overrides category discount)</span>
                </Label>
                <p className="text-xs text-gray-500 mb-3">Set specific discount for individual items</p>
                <div className="space-y-2 max-h-[150px] overflow-y-auto">
                  {menuItems.slice(0, 20).map((item) => {
                    const itemDiscount = discountForm.itemDiscounts?.[item.id];
                    const currentPct = itemDiscount?.percent || 0;
                    return (
                      <div key={item.id} className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium truncate block">{item.name}</span>
                          <span className="text-[10px] text-gray-400">{item.category} • ₹{item.price}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Input 
                            type="number"
                            min="0"
                            max="100"
                            value={currentPct || ''}
                            placeholder="0"
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setDiscountForm(prev => ({
                                ...prev,
                                itemDiscounts: {
                                  ...prev.itemDiscounts,
                                  [item.id]: { name: item.name, percent: val }
                                }
                              }));
                            }}
                            className="w-20 h-8 text-center"
                          />
                          <span className="text-sm text-gray-500">%</span>
                        </div>
                      </div>
                    );
                  })}
                  {menuItems.length > 20 && (
                    <p className="text-xs text-gray-400 text-center py-2">Showing first 20 items...</p>
                  )}
                </div>
              </div>

              {/* OVERALL/FALLBACK BILL DISCOUNT */}
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <Label className="text-sm font-medium">Overall Bill Discount (Fallback)</Label>
                <p className="text-xs text-gray-500 mb-2">Only applies if item has no category or item-specific discount</p>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number"
                    min="0"
                    max="100"
                    value={discountForm.billDiscount || ''}
                    placeholder="0"
                    onChange={(e) => setDiscountForm({ ...discountForm, billDiscount: parseFloat(e.target.value) || 0 })}
                    className="w-24 h-8 text-center"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>

              {/* PRIORITY RULE INFO */}
              <div className="bg-gray-100 p-3 rounded-lg text-xs text-gray-600">
                <p className="font-medium mb-1">📋 Discount Priority Rule:</p>
                <p>Item Discount → Category Discount → Bill Discount</p>
                <p className="text-gray-400 mt-1">Item-level overrides category, category overrides bill</p>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountForm(false)}>Cancel</Button>
            <Button className="bg-green-500" onClick={handleSaveDiscount}>
              {editingDiscount ? 'Update' : 'Create'} Discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu On/Off Modal */}
      <Dialog open={showModule === 'menu-on-off'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ToggleLeft className="h-5 w-5" /> Menu Items On/Off
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 mb-2">
            🔘 Toggle items on/off. OFF items show in grayscale on POS. 🗑️ Delete permanently removes items.
          </p>
          <ScrollArea className="max-h-[60vh]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Toggle</TableHead>
                  <TableHead>Delete</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {menuItems.map((item) => (
                  <TableRow key={item.id} className={!item.active ? 'bg-gray-100 opacity-60' : ''}>
                    <TableCell className={`font-medium ${!item.active ? 'text-gray-400' : ''}`}>
                      {item.name}
                    </TableCell>
                    <TableCell className={!item.active ? 'text-gray-400' : ''}>{item.category}</TableCell>
                    <TableCell className={!item.active ? 'text-gray-400' : ''}>{formatCurrency(item.price)}</TableCell>
                    <TableCell>
                      <Badge className={item.active ? 'bg-green-500' : 'bg-gray-400'}>
                        {item.active ? 'ON' : 'OFF'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch 
                        checked={item.active}
                        onCheckedChange={() => handleToggleMenuItem(item.id, item.active)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        className="h-7"
                        onClick={() => handleDeleteMenuItem(item.id, item.name)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Day End Modal */}
      <Dialog open={showModule === 'day-end'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" /> Day End & Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Day Time Settings */}
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-4 space-y-3">
                <p className="font-semibold text-blue-800 flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Day Operation Times
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-gray-600">Day Start Time</Label>
                    <Input 
                      type="time" 
                      value={daySettings.day_start_time || '09:00'}
                      onChange={(e) => setDaySettings({...daySettings, day_start_time: e.target.value})}
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Day End Time</Label>
                    <Input 
                      type="time" 
                      value={daySettings.day_end_time || '23:00'}
                      onChange={(e) => setDaySettings({...daySettings, day_end_time: e.target.value})}
                      className="bg-white"
                    />
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={async () => {
                    try {
                      await apiClient.post('/day-operations/settings', {
                        store_id: user?.store_id,
                        day_start_time: daySettings.day_start_time,
                        day_end_time: daySettings.day_end_time
                      });
                      toast.success('Day times saved!');
                    } catch (e) {
                      toast.error('Failed to save settings');
                    }
                  }}
                >
                  Save Day Times
                </Button>
              </CardContent>
            </Card>

            {/* Day Status */}
            <Card className={`border-2 ${daySettings.is_day_started ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold">Day Status</p>
                    <p className={`text-sm ${daySettings.is_day_started ? 'text-green-600' : 'text-gray-600'}`}>
                      {daySettings.is_day_started ? '🟢 Day is ACTIVE' : '⚪ Day not started'}
                    </p>
                  </div>
                  {!daySettings.is_day_started && (
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={async () => {
                        try {
                          await apiClient.post('/day-operations/start-day', { store_id: user?.store_id });
                          setDaySettings({...daySettings, is_day_started: true});
                          toast.success('Day started!');
                        } catch (e) {
                          toast.error('Failed to start day');
                        }
                      }}
                    >
                      Start Day
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Today's Summary */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <p className="font-semibold flex items-center gap-2">
                  📅 Today&apos;s Summary 
                  <span className="text-xs text-gray-500 font-normal">({new Date().toLocaleDateString('en-IN')})</span>
                </p>
                {(() => {
                  // Filter only today's orders - compare by date string (YYYY-MM-DD) to avoid timezone issues
                  const todayStr = new Date().toISOString().split('T')[0];
                  const todayOrders = orders.filter(o => {
                    if (!o.created_at) return false;
                    // Extract just the date part from created_at (handles both ISO and other formats)
                    const orderDateStr = o.created_at.substring(0, 10);
                    return orderDateStr === todayStr;
                  });
                  const todayPending = todayOrders.filter(o => !['SERVED', 'PICKED_UP', 'DELIVERED', 'CANCELLED'].includes(o.status));
                  const todayCancelled = todayOrders.filter(o => o.status === 'CANCELLED');
                  const todayCompleted = todayOrders.filter(o => ['SERVED', 'PICKED_UP', 'DELIVERED'].includes(o.status));
                  const todaySales = todayCompleted.reduce((sum, o) => sum + (o.total || 0), 0);
                  
                  return (
                    <>
                      <div className="flex justify-between">
                        <span>Total Orders</span>
                        <span className="font-bold">{todayOrders.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Completed Orders</span>
                        <span className="font-bold text-green-600">{todayCompleted.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Pending Orders</span>
                        <span className="font-bold text-orange-600">{todayPending.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Cancelled Orders</span>
                        <span className="font-bold text-red-600">{todayCancelled.length}</span>
                      </div>
                      <div className="border-t pt-3 flex justify-between">
                        <span className="font-medium">Today&apos;s Sales</span>
                        <span className="font-bold text-xl text-green-600">{formatCurrency(todaySales)}</span>
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800 font-medium">⚠️ End of Day Warning</p>
              <p className="text-xs text-yellow-700 mt-1">
                Closing the day will:
                <br/>• Auto-complete all READY orders
                <br/>• Cancel all pending/preparing orders  
                <br/>• Archive all cancelled orders
                <br/>• Clear the Kitchen Display
                <br/>• Auto-download Excel report
              </p>
            </div>
            
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700" 
              onClick={async () => {
                // Filter only today's orders
                const today = new Date().toDateString();
                const todayOrders = orders.filter(o => new Date(o.created_at).toDateString() === today);
                const pendingCount = todayOrders.filter(o => 
                  !['SERVED', 'PICKED_UP', 'DELIVERED', 'CANCELLED'].includes(o.status)
                ).length;
                const cancelledCount = todayOrders.filter(o => o.status === 'CANCELLED').length;
                
                if (!confirm(`Are you sure you want to close the day?\n\nToday's Summary:\n• ${todayOrders.length} total order(s)\n• ${pendingCount} pending order(s) will be processed\n• ${cancelledCount} cancelled order(s) will be archived\n\nThis action cannot be undone.`)) {
                  return;
                }
                
                try {
                  // Call day-end API
                  const response = await apiClient.post('/day-operations/end-day', {
                    store_id: user?.store_id
                  });
                  
                  const summary = response.data.summary;
                  toast.success(`Day closed! ${summary.orders_auto_closed} completed, ${summary.orders_auto_cancelled} cancelled, ${summary.orders_archived} archived.`);
                  
                  // Auto-download Excel report
                  try {
                    const reportResponse = await apiClient.get(`/day-operations/export-report/${user?.store_id}`, {
                      responseType: 'blob'
                    });
                    
                    // Create download link
                    const url = window.URL.createObjectURL(new Blob([reportResponse.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `Day_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    
                    toast.success('Report downloaded!');
                  } catch (reportError) {
                    console.error('Report download error:', reportError);
                    toast.error('Day closed but report download failed');
                  }
                  
                  // Refresh data
                  loadDashboardData(user?.store_id);
                  setShowModule(null);
                } catch (error) {
                  console.error('Day end error:', error);
                  toast.error(error.response?.data?.detail || 'Failed to close day');
                }
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              Close Day & Download Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Day Start Popup on First Login */}
      <Dialog open={showDayStartPopup} onOpenChange={setShowDayStartPopup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Sun className="h-5 w-5" /> Start Your Day
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-center">
              <p className="text-lg font-medium">Please set your day operation times</p>
              <p className="text-sm text-gray-500 mt-1">Configure when your business day starts and ends</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Day Start Time</Label>
                <Input 
                  type="time" 
                  value={daySettings.day_start_time || '09:00'}
                  onChange={(e) => setDaySettings({...daySettings, day_start_time: e.target.value})}
                />
              </div>
              <div>
                <Label>Day End Time</Label>
                <Input 
                  type="time" 
                  value={daySettings.day_end_time || '23:00'}
                  onChange={(e) => setDaySettings({...daySettings, day_end_time: e.target.value})}
                />
              </div>
            </div>
            
            <Button 
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={async () => {
                try {
                  // Save settings
                  await apiClient.post('/day-operations/settings', {
                    store_id: user?.store_id,
                    day_start_time: daySettings.day_start_time,
                    day_end_time: daySettings.day_end_time
                  });
                  
                  // Start day
                  await apiClient.post('/day-operations/start-day', { store_id: user?.store_id });
                  
                  setDaySettings({...daySettings, is_day_started: true});
                  setShowDayStartPopup(false);
                  toast.success('Day started successfully!');
                } catch (e) {
                  toast.error('Failed to start day');
                }
              }}
            >
              <Sun className="h-4 w-4 mr-2" />
              Save & Start Day
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setShowDayStartPopup(false)}
            >
              Remind Me Later
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Menu Management Modal */}
      <Dialog open={showModule === 'menu'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <UtensilsCrossed className="h-5 w-5" /> Menu Management
              </span>
              <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={handleAddMenuItem}>
                <Plus className="h-4 w-4 mr-1" /> Add New Item
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4 flex-wrap h-auto">
              <TabsTrigger value="all">All Items ({menuItems.length})</TabsTrigger>
              {[...new Set(menuItems.map(item => item.category).filter(Boolean))].sort().map(cat => (
                <TabsTrigger key={cat} value={cat}>
                  {cat} ({menuItems.filter(i => i.category === cat).length})
                </TabsTrigger>
              ))}
            </TabsList>
            
            <TabsContent value="all">
              <ScrollArea className="h-[50vh]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {menuItems.map((item) => (
                      <TableRow key={item.id} className={!item.active ? 'bg-gray-50 opacity-60' : ''}>
                        <TableCell className="font-medium">
                          {item.name}
                          {item.is_combo && (
                            <Badge className="ml-2 bg-purple-500 text-xs">
                              <Package className="h-3 w-3 mr-1" /> Combo
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell>{formatCurrency(item.price)}</TableCell>
                        <TableCell className="font-mono text-xs">{item.short_code || '-'}</TableCell>
                        <TableCell>
                          <Badge className={item.active ? 'bg-green-500' : 'bg-gray-400'}>
                            {item.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="h-7" onClick={() => handleEditMenuItem(item)}>
                              <Settings className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7" onClick={() => handleToggleMenuItem(item.id, item.active)}>
                              <ToggleLeft className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="destructive" className="h-7" onClick={() => handleDeleteMenuItem(item.id, item.name)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>
            
            {[...new Set(menuItems.map(item => item.category).filter(Boolean))].sort().map(cat => (
              <TabsContent key={cat} value={cat}>
                <ScrollArea className="h-[50vh]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {menuItems.filter(i => i.category === cat).map((item) => (
                        <TableRow key={item.id} className={!item.active ? 'bg-gray-50 opacity-60' : ''}>
                          <TableCell className="font-medium">
                            {item.name}
                            {item.is_combo && (
                              <Badge className="ml-2 bg-purple-500 text-xs">
                                <Package className="h-3 w-3 mr-1" /> Combo
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>{formatCurrency(item.price)}</TableCell>
                          <TableCell className="font-mono text-xs">{item.short_code || '-'}</TableCell>
                          <TableCell>
                            <Badge className={item.active ? 'bg-green-500' : 'bg-gray-400'}>
                              {item.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-7" onClick={() => handleEditMenuItem(item)}>
                                <Settings className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="outline" className="h-7" onClick={() => handleToggleMenuItem(item.id, item.active)}>
                                <ToggleLeft className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="destructive" className="h-7" onClick={() => handleDeleteMenuItem(item.id, item.name)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </TabsContent>
            ))}
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Menu Item Form Dialog */}
      <Dialog open={showMenuItemForm} onOpenChange={() => setShowMenuItemForm(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMenuItem ? 'Edit Menu Item' : 'Add New Menu Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Item Name *</Label>
              <Input 
                placeholder="e.g., Margherita Pizza"
                value={menuItemForm.name}
                onChange={(e) => setMenuItemForm({ ...menuItemForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Category *</Label>
              <div className="flex gap-2">
                <Select 
                  value={menuItemForm.category} 
                  onValueChange={(v) => {
                    // Auto-enable combo mode if category is Combo/Combos
                    const isComboCategory = v.toLowerCase() === 'combo' || v.toLowerCase() === 'combos';
                    setMenuItemForm({ 
                      ...menuItemForm, 
                      category: v,
                      is_combo: isComboCategory ? true : menuItemForm.is_combo
                    });
                  }}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.sort().map(cat => (
                      <SelectItem key={cat} value={cat}>
                        {cat} {(cat.toLowerCase() === 'combo' || cat.toLowerCase() === 'combos') && '🎁'}
                      </SelectItem>
                    ))}
                    {newCategoryName && !categories.includes(newCategoryName) && (
                      <SelectItem value={newCategoryName}>{newCategoryName} (New)</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    const name = prompt('Enter new category name:');
                    if (name && name.trim()) {
                      setNewCategoryName(name.trim());
                      setMenuItemForm({ ...menuItemForm, category: name.trim() });
                    }
                  }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price (₹) *</Label>
                <Input 
                  type="number"
                  placeholder="99"
                  value={menuItemForm.price}
                  onChange={(e) => setMenuItemForm({ ...menuItemForm, price: e.target.value })}
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <Label>Short Code</Label>
                <Input 
                  placeholder="e.g., MP01"
                  value={menuItemForm.short_code}
                  onChange={(e) => setMenuItemForm({ ...menuItemForm, short_code: e.target.value.toUpperCase() })}
                  maxLength={6}
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                placeholder="Item description (optional)"
                value={menuItemForm.description}
                onChange={(e) => setMenuItemForm({ ...menuItemForm, description: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={menuItemForm.active}
                onCheckedChange={(checked) => setMenuItemForm({ ...menuItemForm, active: checked })}
              />
              <Label>Active (available for ordering)</Label>
            </div>
            
            {/* Combo Section */}
            <div className="border-t pt-4 mt-4">
              {(menuItemForm.category?.toLowerCase() === 'combo' || menuItemForm.category?.toLowerCase() === 'combos') && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-purple-800 font-medium">🎁 Combo Category Selected</p>
                  <p className="text-xs text-purple-600 mt-1">
                    Items in the Combo category are automatically treated as combo/bundle items. 
                    Add the items included in this combo below.
                  </p>
                </div>
              )}
              <div className="flex items-center gap-2 mb-3">
                <Switch 
                  checked={menuItemForm.is_combo}
                  onCheckedChange={(checked) => setMenuItemForm({ ...menuItemForm, is_combo: checked, combo_items: checked ? menuItemForm.combo_items : [] })}
                />
                <Label className="flex items-center gap-1">
                  <Package className="h-4 w-4" /> 
                  This is a Combo/Bundle
                </Label>
              </div>
              
              {menuItemForm.is_combo && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium text-blue-800">Combo Items</p>
                    <Badge variant="outline" className="bg-white">
                      {menuItemForm.combo_items.length} items
                    </Badge>
                  </div>
                  
                  {/* Add Item to Combo */}
                  <div className="flex gap-2">
                    <Select 
                      onValueChange={(itemId) => {
                        const selectedItem = menuItems.find(i => i.id === itemId);
                        if (selectedItem && !menuItemForm.combo_items.find(c => c.item_id === itemId)) {
                          setMenuItemForm({
                            ...menuItemForm,
                            combo_items: [
                              ...menuItemForm.combo_items,
                              { item_id: itemId, item_name: selectedItem.name, quantity: 1 }
                            ]
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="flex-1 bg-white">
                        <SelectValue placeholder="Add item to combo..." />
                      </SelectTrigger>
                      <SelectContent>
                        {menuItems
                          .filter(i => i.active && !i.is_combo && !menuItemForm.combo_items.find(c => c.item_id === i.id))
                          .map(item => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name} - ₹{item.price}
                            </SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Combo Items List */}
                  {menuItemForm.combo_items.length > 0 ? (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {menuItemForm.combo_items.map((comboItem, idx) => (
                        <div key={comboItem.item_id} className="flex items-center justify-between bg-white p-2 rounded border">
                          <span className="text-sm font-medium truncate flex-1">{comboItem.item_name}</span>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-6 w-6 p-0"
                              onClick={() => {
                                const updated = [...menuItemForm.combo_items];
                                if (updated[idx].quantity > 1) {
                                  updated[idx].quantity -= 1;
                                  setMenuItemForm({ ...menuItemForm, combo_items: updated });
                                }
                              }}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-6 text-center text-sm font-bold">{comboItem.quantity}</span>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-6 w-6 p-0"
                              onClick={() => {
                                const updated = [...menuItemForm.combo_items];
                                updated[idx].quantity += 1;
                                setMenuItemForm({ ...menuItemForm, combo_items: updated });
                              }}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              onClick={() => {
                                setMenuItemForm({
                                  ...menuItemForm,
                                  combo_items: menuItemForm.combo_items.filter((_, i) => i !== idx)
                                });
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-blue-600 text-center py-2">
                      No items added yet. Select items above to build your combo.
                    </p>
                  )}
                  
                  {/* Individual Items Price vs Combo Price */}
                  {menuItemForm.combo_items.length > 0 && (
                    <div className="text-xs text-blue-700 bg-blue-100 p-2 rounded">
                      <div className="flex justify-between">
                        <span>Individual items total:</span>
                        <span className="line-through">
                          ₹{menuItemForm.combo_items.reduce((sum, ci) => {
                            const item = menuItems.find(m => m.id === ci.item_id);
                            return sum + (item?.price || 0) * ci.quantity;
                          }, 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between font-bold">
                        <span>Combo price:</span>
                        <span className="text-green-700">₹{menuItemForm.price || '0.00'}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMenuItemForm(false)}>Cancel</Button>
            <Button className="bg-green-500" onClick={handleSaveMenuItem}>
              {editingMenuItem ? 'Update Item' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Categories Management Modal */}
      <Dialog open={showModule === 'categories' || showModule === 'categories-config'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ListOrdered className="h-5 w-5" /> Category Management
              </span>
              <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => {
                const name = prompt('Enter new category name:');
                if (name && name.trim()) {
                  handleAddCategory(name.trim());
                }
              }}>
                <Plus className="h-4 w-4 mr-1" /> Add Category
              </Button>
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {(() => {
              // Use categories from API (includes both menu item categories and custom categories)
              const allCategories = categories.length > 0 ? categories : [];
              return allCategories.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No categories found
                  <Button variant="outline" className="mt-4 block mx-auto" onClick={() => {
                    const name = prompt('Enter new category name:');
                    if (name && name.trim()) {
                      handleAddCategory(name.trim());
                    }
                  }}>
                    <Plus className="h-4 w-4 mr-1" /> Add First Category
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category Name</TableHead>
                      <TableHead>Items Count</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allCategories.map((category) => {
                      const itemCount = menuItems.filter(item => item.category === category).length;
                      return (
                        <TableRow key={category}>
                          <TableCell className="font-medium">{category}</TableCell>
                          <TableCell>{itemCount} items</TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="destructive" 
                              className="h-7"
                              onClick={() => handleDeleteCategory(category)}
                            >
                              <Trash2 className="h-3 w-3 mr-1" /> Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              );
            })()}
          </ScrollArea>
          <div className="text-xs text-gray-500 mt-2">
            ⚠️ Deleting a category will permanently remove all items in that category.
          </div>
        </DialogContent>
      </Dialog>

      {/* Offers Manager */}
      <OffersManager 
        storeId={user?.store_id} 
        isOpen={showOffersManager} 
        onClose={() => setShowOffersManager(false)} 
      />

      {/* Customer History Lookup */}
      <CustomerHistoryLookup
        storeId={user?.store_id}
        isOpen={showCustomerLookup}
        onClose={() => setShowCustomerLookup(false)}
        onSelectCustomer={(customer) => {
          toast.success(`Customer ${customer.name} selected!`);
          setShowCustomerLookup(false);
        }}
      />
    </div>
  );
};

export default POSDashboard;
